import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const GestionarFormulario = () => {
  const { formularioId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [formulario, setFormulario] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPregunta, setNewPregunta] = useState({
    titulo: '',
    tipo: 'opcion_multiple',
    opciones: ['', ''],
    opciones_puntajes: {},
    respuesta_correcta: '',
    puntuacion: 1.0,
    orden: 1,
    video_url: '',
    wix_url: '',
    formulario_publico_url: ''
  });
  const [creating, setCreating] = useState(false);
  const [editingPregunta, setEditingPregunta] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadData();
  }, [formularioId]);

  const loadData = async () => {
    try {
      const [formularioRes, preguntasRes] = await Promise.all([
        axios.get(`${API}/formularios/${formularioId}`),
        axios.get(`${API}/formularios/${formularioId}/preguntas`)
      ]);
      
      setFormulario(formularioRes.data);
      setPreguntas(preguntasRes.data);
    } catch (error) {
      toast.error('Error al cargar formulario');
      navigate('/app/clases');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePregunta = async (e) => {
    e.preventDefault();
    if (!newPregunta.titulo) {
      toast.error('El título de la pregunta es obligatorio');
      return;
    }

    setCreating(true);
    try {
      const preguntaData = {
        ...newPregunta,
        orden: preguntas.length + 1
      };
      
      console.log('DEBUG: Enviando pregunta con tipo:', preguntaData.tipo);
      console.log('DEBUG: Datos completos:', preguntaData);
      
      await axios.post(`${API}/formularios/${formularioId}/preguntas`, preguntaData);
      
      setNewPregunta({
        titulo: '',
        tipo: 'opcion_multiple',
        opciones: ['', ''],
        opciones_puntajes: {},
        respuesta_correcta: '',
        puntuacion: 1.0,
        orden: 1,
        video_url: '',
        wix_url: '',
        formulario_publico_url: ''
      });
      
      setIsDialogOpen(false);
      toast.success('Pregunta agregada exitosamente');
      loadData(); // Reload to show updated questions
    } catch (error) {
      toast.error('Error al crear pregunta');
    } finally {
      setCreating(false);
    }
  };

  const addOpcion = () => {
    setNewPregunta({
      ...newPregunta,
      opciones: [...newPregunta.opciones, '']
    });
  };

  const removeOpcion = (index) => {
    if (newPregunta.opciones.length > 2) {
      const opciones = newPregunta.opciones.filter((_, i) => i !== index);
      setNewPregunta({ ...newPregunta, opciones });
    }
  };

  const updateOpcion = (index, value) => {
    const opciones = [...newPregunta.opciones];
    opciones[index] = value;
    setNewPregunta({ ...newPregunta, opciones });
  };

  // Funciones para escala_valoracion
  const addOpcionEscala = () => {
    setNewPregunta({
      ...newPregunta,
      opciones: [...newPregunta.opciones, '']
    });
  };

  const removeOpcionEscala = (index) => {
    if (newPregunta.opciones.length > 2) {
      const opciones = newPregunta.opciones.filter((_, i) => i !== index);
      const nuevoPuntajes = { ...newPregunta.opciones_puntajes };
      delete nuevoPuntajes[newPregunta.opciones[index]];
      setNewPregunta({ ...newPregunta, opciones, opciones_puntajes: nuevoPuntajes });
    }
  };

  const updateOpcionEscala = (index, value, puntaje) => {
    const opciones = [...newPregunta.opciones];
    const oldValue = opciones[index];
    opciones[index] = value;
    
    const nuevoPuntajes = { ...newPregunta.opciones_puntajes };
    if (oldValue) delete nuevoPuntajes[oldValue];
    if (value) nuevoPuntajes[value] = puntaje || 0;
    
    setNewPregunta({ ...newPregunta, opciones, opciones_puntajes: nuevoPuntajes });
  };

  const getTipoDisplayName = (tipo) => {
    const tipos = {
      'opcion_multiple': 'Opción Múltiple',
      'respuesta_corta': 'Respuesta Corta',
      'respuesta_larga': 'Respuesta Larga',
      'ensayo': 'Ensayo',
      'verdadero_falso': 'Verdadero/Falso',
      'fecha': 'Fecha',
      'subir_audio': 'Subir Audio',
      'subir_video': 'Subir Video',
      'subir_foto': 'Subir Foto',
      'link_wix': 'Link de Formulario Wix',
      'link_youtube': 'Link de Video (YouTube/Vimeo)',
      'link_formulario_interno': 'Link de Formulario Interno (IBA)',
      'seleccion_estudiante': 'Selección de Estudiante',
      'escala_valoracion': 'Escala de Valoración (con puntajes)',
      'firma': 'Firma Dibujada'
    };
    return tipos[tipo] || tipo;
  };

  const openEditPregunta = (pregunta) => {
    setEditingPregunta({
      ...pregunta,
      opciones: pregunta.opciones || ['', '']
    });
    setIsEditDialogOpen(true);
  };

  const handleEditPregunta = async (e) => {
    e.preventDefault();
    if (!editingPregunta.titulo) {
      toast.error('El título de la pregunta es obligatorio');
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`${API}/preguntas/${editingPregunta.id}`, {
        titulo: editingPregunta.titulo,
        tipo: editingPregunta.tipo,
        opciones: editingPregunta.opciones,
        respuesta_correcta: editingPregunta.respuesta_correcta,
        puntuacion: editingPregunta.puntuacion
      });
      
      setIsEditDialogOpen(false);
      setEditingPregunta(null);
      toast.success('Pregunta actualizada exitosamente');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar pregunta');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeletePregunta = async (preguntaId, preguntaTitulo) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la pregunta "${preguntaTitulo}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/preguntas/${preguntaId}`);
      toast.success('Pregunta eliminada exitosamente');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar pregunta');
    }
  };

  const updateEditOpcion = (index, value) => {
    const opciones = [...editingPregunta.opciones];
    opciones[index] = value;
    setEditingPregunta({ ...editingPregunta, opciones });
  };

  const addEditOpcion = () => {
    setEditingPregunta({
      ...editingPregunta,
      opciones: [...editingPregunta.opciones, '']
    });
  };

  const removeEditOpcion = (index) => {
    if (editingPregunta.opciones.length > 2) {
      const opciones = editingPregunta.opciones.filter((_, i) => i !== index);
      setEditingPregunta({ ...editingPregunta, opciones });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!formulario) {
    return (
      <div className="space-y-8">
        <Card className="shadow-lg border-0">
          <CardContent className="text-center py-12">
            <i className="fas fa-exclamation-triangle text-gray-300 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Formulario no encontrado</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="gestionar-formulario-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Button
            variant="ghost"
            onClick={() => {
              // Navegación condicional: independientes van a formularios-independientes, clase-relacionados van a clases
              if (formulario.tipo === 'independiente' || !formulario.clase_id) {
                navigate('/app/formularios-independientes');
              } else {
                navigate(`/app/clases/${formulario.clase_id}/formularios`);
              }
            }}
            className="mb-4 text-blue-600 hover:text-blue-800"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver a Formularios
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Gestionar Formulario
          </h1>
          <p className="text-gray-600">{formulario.titulo}</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="mt-4 lg:mt-0 instituto-gradient btn-hover text-white" data-testid="add-pregunta-button">
              <i className="fas fa-plus mr-2"></i>
              Agregar Pregunta
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-blue-50 via-white to-indigo-50">
            <DialogHeader className="border-b pb-4 mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 -m-6 mb-6 rounded-t-lg">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 p-3 rounded-lg">
                  <i className="fas fa-plus-circle text-3xl"></i>
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold text-white">Agregar Nueva Pregunta</DialogTitle>
                  <p className="text-blue-100 text-sm mt-1">Crea una pregunta con formato profesional</p>
                </div>
              </div>
            </DialogHeader>
            <form onSubmit={handleCreatePregunta} className="space-y-6">
              {/* Editor de Pregunta */}
              <div className="space-y-3 bg-white p-5 rounded-xl shadow-md border border-blue-100">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white p-2 rounded-lg">
                    <i className="fas fa-edit"></i>
                  </div>
                  <Label htmlFor="titulo" className="text-lg font-semibold text-gray-800">Pregunta / Texto *</Label>
                </div>
                <div className="border-2 border-blue-200 rounded-lg overflow-hidden shadow-sm" style={{ minHeight: '200px' }}>
                  <ReactQuill
                    theme="snow"
                    value={newPregunta.titulo}
                    onChange={(value) => setNewPregunta({ ...newPregunta, titulo: value })}
                    placeholder="✍️ Escribe aquí la pregunta o texto (puedes usar formato: negritas, cursivas, listas, numerales, etc.)..."
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'indent': '-1'}, { 'indent': '+1' }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    style={{ height: '150px' }}
                  />
                </div>
                <div className="flex items-start space-x-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <i className="fas fa-lightbulb text-blue-600 mt-0.5"></i>
                  <p className="text-xs text-blue-800">
                    <strong>Tip:</strong> Usa el editor para dar formato a tu texto: <strong>negritas</strong>, <em>cursivas</em>, listas con viñetas o numeradas, sangrías, etc.
                  </p>
                </div>
              </div>
              
              {/* Tipo y Puntuación */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3 bg-white p-5 rounded-xl shadow-md border border-purple-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-2 rounded-lg">
                      <i className="fas fa-list-ul"></i>
                    </div>
                    <Label htmlFor="tipo" className="text-lg font-semibold text-gray-800">Tipo de Pregunta</Label>
                  </div>
                  <Select value={newPregunta.tipo} onValueChange={(value) => setNewPregunta({ ...newPregunta, tipo: value })}>
                    <SelectTrigger data-testid="pregunta-tipo-select" className="border-2 border-purple-200 hover:border-purple-400 transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parrafo_lectura">📖 Párrafo de Lectura (solo lectura, no responde)</SelectItem>
                      <SelectItem value="opcion_multiple">Opción Múltiple</SelectItem>
                      <SelectItem value="respuesta_corta">Respuesta Corta</SelectItem>
                      <SelectItem value="respuesta_larga">Respuesta Larga</SelectItem>
                      <SelectItem value="ensayo">Ensayo</SelectItem>
                      <SelectItem value="verdadero_falso">Verdadero/Falso</SelectItem>
                      <SelectItem value="fecha">Fecha</SelectItem>
                      <SelectItem value="seleccion_estudiante">Selección de Estudiante</SelectItem>
                      <SelectItem value="escala_valoracion">⭐ Escala de Valoración (con puntajes)</SelectItem>
                      <SelectItem value="firma">✍️ Firma Dibujada</SelectItem>
                      <SelectItem value="subir_foto">📷 Subir Foto</SelectItem>
                      <SelectItem value="subir_audio">🎤 Subir Audio</SelectItem>
                      <SelectItem value="subir_video">🎬 Subir Video</SelectItem>
                      <SelectItem value="link_wix">Link de Formulario Wix</SelectItem>
                      <SelectItem value="link_formulario_interno">📋 Link de Formulario Interno (IBA)</SelectItem>
                      <SelectItem value="link_youtube">▶️ Link de Video (YouTube/Vimeo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-3 bg-white p-5 rounded-xl shadow-md border border-amber-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-2 rounded-lg">
                      <i className="fas fa-star"></i>
                    </div>
                    <Label htmlFor="puntuacion" className="text-lg font-semibold text-gray-800">Puntuación</Label>
                  </div>
                  <Input
                    id="puntuacion"
                    type="number"
                    step="0.1"
                    min="0"
                    value={newPregunta.tipo === 'parrafo_lectura' ? 0 : newPregunta.puntuacion}
                    onChange={(e) => setNewPregunta({ ...newPregunta, puntuacion: parseFloat(e.target.value) })}
                    disabled={newPregunta.tipo === 'parrafo_lectura'}
                    data-testid="pregunta-puntuacion-input"
                    className="border-2 border-amber-200 hover:border-amber-400 transition-colors"
                  />
                  {newPregunta.tipo === 'parrafo_lectura' && (
                    <div className="flex items-start space-x-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
                      <p className="text-xs text-blue-800">
                        Los párrafos de lectura no se califican (0 puntos)
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Opciones para preguntas de opción múltiple */}
              {(newPregunta.tipo === 'opcion_multiple' || newPregunta.tipo === 'verdadero_falso') && (
                <div className="space-y-2">
                  <Label>Opciones de Respuesta</Label>
                  {newPregunta.tipo === 'verdadero_falso' ? (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Input value="Verdadero" disabled className="flex-1" />
                        <input
                          type="radio"
                          name="respuesta_correcta"
                          checked={newPregunta.respuesta_correcta === 'Verdadero'}
                          onChange={() => setNewPregunta({ ...newPregunta, respuesta_correcta: 'Verdadero' })}
                        />
                        <Label className="text-sm">Correcta</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Input value="Falso" disabled className="flex-1" />
                        <input
                          type="radio"
                          name="respuesta_correcta"
                          checked={newPregunta.respuesta_correcta === 'Falso'}
                          onChange={() => setNewPregunta({ ...newPregunta, respuesta_correcta: 'Falso' })}
                        />
                        <Label className="text-sm">Correcta</Label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {newPregunta.opciones.map((opcion, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Input
                            value={opcion}
                            onChange={(e) => updateOpcion(index, e.target.value)}
                            placeholder={`Opción ${index + 1}`}
                            className="flex-1"
                          />
                          <input
                            type="radio"
                            name="respuesta_correcta"
                            checked={newPregunta.respuesta_correcta === opcion}
                            onChange={() => setNewPregunta({ ...newPregunta, respuesta_correcta: opcion })}
                          />
                          <Label className="text-sm">Correcta</Label>
                          {newPregunta.opciones.length > 2 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeOpcion(index)}
                              className="text-red-600"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addOpcion}
                        className="w-full"
                      >
                        <i className="fas fa-plus mr-2"></i>
                        Agregar Opción
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Opciones para escala de valoración con puntajes */}
              {newPregunta.tipo === 'escala_valoracion' && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-5 shadow-lg">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-purple-500 to-pink-600 text-white p-4 rounded-xl shadow-md">
                        <i className="fas fa-star text-2xl"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-purple-800 text-lg mb-2">⭐ Escala de Valoración</h4>
                        <p className="text-sm text-purple-700 leading-relaxed">
                          Define opciones con puntajes personalizados. La calificación se asigna automáticamente según la opción seleccionada.
                        </p>
                        <div className="bg-white/70 rounded-lg p-3 mt-3 border border-purple-200">
                          <p className="text-xs text-purple-800">
                            <strong>Ejemplo:</strong> Mala (0 pts), Regular (2 pts), Buena (4 pts), Excelente (10 pts)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Label>Opciones y Puntajes</Label>
                  <div className="space-y-3">
                    {newPregunta.opciones.map((opcion, index) => (
                      <div key={index} className="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
                        <div className="flex-1">
                          <Input
                            value={opcion}
                            onChange={(e) => {
                              const oldPuntaje = newPregunta.opciones_puntajes[opcion] || 0;
                              updateOpcionEscala(index, e.target.value, oldPuntaje);
                            }}
                            placeholder={`Opción ${index + 1} (ej: Excelente)`}
                          />
                        </div>
                        <div className="w-32">
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={newPregunta.opciones_puntajes[opcion] || 0}
                            onChange={(e) => {
                              updateOpcionEscala(index, opcion, parseFloat(e.target.value) || 0);
                            }}
                            placeholder="Puntos"
                            className="text-center font-semibold"
                          />
                        </div>
                        <span className="text-sm text-gray-500 w-12">pts</span>
                        {newPregunta.opciones.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeOpcionEscala(index)}
                            className="text-red-600"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addOpcionEscala}
                      className="w-full"
                      disabled={newPregunta.opciones.length >= 10}
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Agregar Opción {newPregunta.opciones.length >= 10 && '(Máximo 10)'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Información para firma dibujada */}
              {newPregunta.tipo === 'firma' && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-xl p-5 shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white p-4 rounded-xl shadow-md">
                      <i className="fas fa-signature text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-indigo-800 text-lg mb-2">✍️ Firma Dibujada</h4>
                      <p className="text-sm text-indigo-700 leading-relaxed">
                        El usuario podrá dibujar su firma usando mouse o touch. Se otorga puntuación completa por cualquier firma válida.
                        Ideal para validar formularios de evaluación o documentos oficiales.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Información para párrafo de lectura */}
              {newPregunta.tipo === 'parrafo_lectura' && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-5 shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white p-4 rounded-xl shadow-md">
                      <i className="fas fa-book-reader text-2xl"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-green-800 text-lg mb-2 flex items-center">
                        📖 Párrafo de Lectura
                        <Badge className="ml-3 bg-green-600 text-white">Solo lectura</Badge>
                      </h4>
                      <p className="text-sm text-green-700 mb-3 leading-relaxed">
                        Este texto es solo para que el estudiante lo lea. <strong>No requiere respuesta</strong> y <strong>no se califica (0 puntos)</strong>.
                        Úsalo para proporcionar contexto, instrucciones o material de lectura antes de las preguntas.
                      </p>
                      <div className="bg-white/50 rounded-lg p-3 border border-green-200">
                        <p className="text-sm text-green-800 font-semibold mb-2">
                          <i className="fas fa-lightbulb text-amber-500 mr-2"></i>Ejemplos de uso:
                        </p>
                        <ul className="text-sm text-green-700 space-y-1 ml-4">
                          <li>• Proporcionar un pasaje bíblico para analizar</li>
                          <li>• Dar instrucciones especiales para las siguientes preguntas</li>
                          <li>• Compartir contexto histórico o teológico</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Information for new question types */}
              {newPregunta.tipo === 'fecha' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-calendar-alt text-blue-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-blue-800">Pregunta de Fecha</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Los estudiantes podrán seleccionar una fecha usando un calendario. 
                        Se otorga puntuación completa por cualquier fecha válida seleccionada.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {newPregunta.tipo === 'subir_audio' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-microphone text-green-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-green-800">Subir Audio</h4>
                      <p className="text-sm text-green-700 mt-1">
                        Los estudiantes podrán grabar o subir archivos de audio (MP3, WAV, M4A). 
                        Máximo 25MB. Se otorga puntuación completa por cualquier archivo válido subido.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {newPregunta.tipo === 'subir_video' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <i className="fas fa-video text-purple-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-purple-800">Subir Video</h4>
                      <p className="text-sm text-purple-700 mt-1">
                        Los estudiantes podrán subir archivos de video (MP4, AVI, MOV, WebM). 
                        Máximo 100MB. Se otorga puntuación completa por cualquier archivo válido subido.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {newPregunta.tipo === 'link_youtube' && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-red-50 to-blue-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex items-center gap-2">
                        <i className="fab fa-youtube text-red-600"></i>
                        <span className="text-gray-400">/</span>
                        <i className="fab fa-vimeo-v text-blue-500"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">Video de YouTube o Vimeo</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Los estudiantes verán el video embebido directamente. Ejemplo: "Ver este video sobre..."
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="video_url" className="text-sm font-medium">
                      URL del Video (YouTube o Vimeo) *
                    </Label>
                    <Input
                      id="video_url"
                      type="url"
                      placeholder="https://youtube.com/watch?v=... o https://vimeo.com/..."
                      value={newPregunta.video_url}
                      onChange={(e) => setNewPregunta({ ...newPregunta, video_url: e.target.value })}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Pega el enlace del video de YouTube o Vimeo que los estudiantes deben ver
                    </p>
                  </div>
                </div>
              )}

              {newPregunta.tipo === 'link_wix' && (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <i className="fas fa-external-link-alt text-yellow-600 mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-yellow-800">Formulario de Wix</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          Los estudiantes verán el formulario embebido directamente para completar. Ejemplo: "Completa este formulario de registro..."
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="wix_url" className="text-sm font-medium">
                      URL del Formulario de Wix *
                    </Label>
                    <Input
                      id="wix_url"
                      type="url"
                      placeholder="https://forms.wix.com/... (enlace del formulario Wix)"
                      value={newPregunta.wix_url || ''}
                      onChange={(e) => setNewPregunta({ ...newPregunta, wix_url: e.target.value })}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Pega el enlace del formulario Wix que los estudiantes deben completar
                    </p>
                  </div>
                </div>
              )}

              {newPregunta.tipo === 'link_formulario_interno' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <i className="fas fa-file-alt text-blue-600 mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-blue-800">Formulario Interno de IBA</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Los estudiantes verán el formulario público de IBA embebido directamente. Ideal para encuestas, fichas de datos, etc.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="formulario_publico_url" className="text-sm font-medium">
                      URL del Formulario Público de IBA *
                    </Label>
                    <Input
                      id="formulario_publico_url"
                      type="url"
                      placeholder="https://tudominio.com/formulario-publico/abc123..."
                      value={newPregunta.formulario_publico_url || ''}
                      onChange={(e) => setNewPregunta({ ...newPregunta, formulario_publico_url: e.target.value })}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Pega el enlace público del formulario creado en "Formularios Independientes"
                    </p>
                  </div>
                </div>
              )}
              
              {/* Botones de Acción */}
              <div className="flex justify-end space-x-3 pt-6 border-t-2 border-gray-200 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={creating}
                  className="px-6 py-3 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="instituto-gradient text-white px-6 py-3 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  disabled={creating}
                  data-testid="pregunta-submit-button"
                >
                  {creating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Agregando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check-circle mr-2"></i>
                      Agregar Pregunta
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Pregunta Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-purple-50 via-white to-pink-50">
            <DialogHeader className="border-b pb-4 mb-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 -m-6 mb-6 rounded-t-lg">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 p-3 rounded-lg">
                  <i className="fas fa-edit text-3xl"></i>
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold text-white">Editar Pregunta</DialogTitle>
                  <p className="text-purple-100 text-sm mt-1">Modifica los detalles de tu pregunta</p>
                </div>
              </div>
            </DialogHeader>
            {editingPregunta && (
              <form onSubmit={handleEditPregunta} className="space-y-6">
                {/* Editor de Pregunta */}
                <div className="space-y-3 bg-white p-5 rounded-xl shadow-md border border-purple-100">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-2 rounded-lg">
                      <i className="fas fa-edit"></i>
                    </div>
                    <Label htmlFor="edit-titulo" className="text-lg font-semibold text-gray-800">Pregunta / Texto</Label>
                  </div>
                  <div className="border-2 border-purple-200 rounded-lg overflow-hidden shadow-sm" style={{ minHeight: '200px' }}>
                    <ReactQuill
                      theme="snow"
                      value={editingPregunta.titulo}
                      onChange={(value) => setEditingPregunta({ ...editingPregunta, titulo: value })}
                      placeholder="✍️ Escribe aquí la pregunta o texto (puedes usar formato: negritas, cursivas, listas, numerales, etc.)..."
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          [{ 'indent': '-1'}, { 'indent': '+1' }],
                          ['link'],
                          ['clean']
                        ]
                      }}
                      style={{ height: '150px' }}
                    />
                  </div>
                </div>
                
                {/* Tipo y Puntuación */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3 bg-white p-5 rounded-xl shadow-md border border-purple-100">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-2 rounded-lg">
                        <i className="fas fa-list-ul"></i>
                      </div>
                      <Label htmlFor="edit-tipo" className="text-lg font-semibold text-gray-800">Tipo de Pregunta</Label>
                    </div>
                    <Select 
                      value={editingPregunta.tipo} 
                      onValueChange={(value) => setEditingPregunta({ 
                        ...editingPregunta, 
                        tipo: value,
                        opciones: value === 'verdadero_falso' ? ['Verdadero', 'Falso'] : 
                                 (value === 'opcion_multiple' ? ['', ''] : [])
                      })}
                    >
                      <SelectTrigger className="border-2 border-purple-200 hover:border-purple-400 transition-colors">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="parrafo_lectura">📖 Párrafo de Lectura (solo lectura, no responde)</SelectItem>
                        <SelectItem value="opcion_multiple">Opción Múltiple</SelectItem>
                        <SelectItem value="respuesta_corta">Respuesta Corta</SelectItem>
                        <SelectItem value="respuesta_larga">Respuesta Larga</SelectItem>
                        <SelectItem value="ensayo">Ensayo</SelectItem>
                        <SelectItem value="verdadero_falso">Verdadero/Falso</SelectItem>
                        <SelectItem value="fecha">Fecha</SelectItem>
                        <SelectItem value="seleccion_estudiante">Selección de Estudiante</SelectItem>
                        <SelectItem value="subir_foto">📷 Subir Foto</SelectItem>
                        <SelectItem value="subir_audio">🎤 Subir Audio</SelectItem>
                        <SelectItem value="subir_video">🎬 Subir Video</SelectItem>
                        <SelectItem value="link_wix">Link de Formulario Wix</SelectItem>
                        <SelectItem value="link_formulario_interno">📋 Link de Formulario Interno (IBA)</SelectItem>
                        <SelectItem value="link_youtube">▶️ Link de Video (YouTube/Vimeo)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-3 bg-white p-5 rounded-xl shadow-md border border-amber-100">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-2 rounded-lg">
                        <i className="fas fa-star"></i>
                      </div>
                      <Label htmlFor="edit-puntuacion" className="text-lg font-semibold text-gray-800">Puntuación</Label>
                    </div>
                    <Input
                      id="edit-puntuacion"
                      type="number"
                      step="0.1"
                      min="0"
                      value={editingPregunta.puntuacion}
                      onChange={(e) => setEditingPregunta({ ...editingPregunta, puntuacion: parseFloat(e.target.value) })}
                      className="border-2 border-amber-200 hover:border-amber-400 transition-colors"
                    />
                  </div>
                </div>
                
                {/* Opciones para preguntas de opción múltiple */}
                {(editingPregunta.tipo === 'opcion_multiple' || editingPregunta.tipo === 'verdadero_falso') && (
                  <div className="space-y-2">
                    <Label>Opciones de Respuesta</Label>
                    {editingPregunta.tipo === 'verdadero_falso' ? (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Input value="Verdadero" disabled className="flex-1" />
                          <input
                            type="radio"
                            name="edit_respuesta_correcta"
                            checked={editingPregunta.respuesta_correcta === 'Verdadero'}
                            onChange={() => setEditingPregunta({ ...editingPregunta, respuesta_correcta: 'Verdadero' })}
                          />
                          <Label className="text-sm">Correcta</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input value="Falso" disabled className="flex-1" />
                          <input
                            type="radio"
                            name="edit_respuesta_correcta"
                            checked={editingPregunta.respuesta_correcta === 'Falso'}
                            onChange={() => setEditingPregunta({ ...editingPregunta, respuesta_correcta: 'Falso' })}
                          />
                          <Label className="text-sm">Correcta</Label>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {editingPregunta.opciones.map((opcion, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <Input
                              value={opcion}
                              onChange={(e) => updateEditOpcion(index, e.target.value)}
                              placeholder={`Opción ${index + 1}`}
                              className="flex-1"
                            />
                            <input
                              type="radio"
                              name="edit_respuesta_correcta"
                              checked={editingPregunta.respuesta_correcta === opcion}
                              onChange={() => setEditingPregunta({ ...editingPregunta, respuesta_correcta: opcion })}
                            />
                            <Label className="text-sm">Correcta</Label>
                            {editingPregunta.opciones.length > 2 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeEditOpcion(index)}
                                className="text-red-600"
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addEditOpcion}
                          className="w-full"
                        >
                          <i className="fas fa-plus mr-2"></i>
                          Agregar Opción
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {editingPregunta.tipo === 'link_youtube' && (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-red-50 to-blue-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex items-center gap-2">
                          <i className="fab fa-youtube text-red-600"></i>
                          <span className="text-gray-400">/</span>
                          <i className="fab fa-vimeo-v text-blue-500"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800">Video de YouTube o Vimeo</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Los estudiantes verán el video embebido directamente.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_video_url" className="text-sm font-medium">
                        URL del Video (YouTube o Vimeo) *
                      </Label>
                      <Input
                        id="edit_video_url"
                        type="url"
                        placeholder="https://youtube.com/watch?v=... o https://vimeo.com/..."
                        value={editingPregunta.video_url || ''}
                        onChange={(e) => setEditingPregunta({ ...editingPregunta, video_url: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {editingPregunta.tipo === 'link_wix' && (
                  <div className="space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <i className="fas fa-external-link-alt text-yellow-600 mt-1"></i>
                        <div>
                          <h4 className="font-semibold text-yellow-800">Formulario de Wix</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            Los estudiantes verán el formulario embebido directamente para completar.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_wix_url" className="text-sm font-medium">
                        URL del Formulario de Wix *
                      </Label>
                      <Input
                        id="edit_wix_url"
                        type="url"
                        placeholder="https://forms.wix.com/... (enlace del formulario Wix)"
                        value={editingPregunta.wix_url || ''}
                        onChange={(e) => setEditingPregunta({ ...editingPregunta, wix_url: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {editingPregunta.tipo === 'link_formulario_interno' && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <i className="fas fa-file-alt text-blue-600 mt-1"></i>
                        <div>
                          <h4 className="font-semibold text-blue-800">Formulario Interno de IBA</h4>
                          <p className="text-sm text-blue-700 mt-1">
                            Los estudiantes verán el formulario público de IBA embebido directamente.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit_formulario_publico_url" className="text-sm font-medium">
                        URL del Formulario Público de IBA *
                      </Label>
                      <Input
                        id="edit_formulario_publico_url"
                        type="url"
                        placeholder="https://tudominio.com/formulario-publico/abc123..."
                        value={editingPregunta.formulario_publico_url || ''}
                        onChange={(e) => setEditingPregunta({ ...editingPregunta, formulario_publico_url: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {/* Botones de Acción */}
                <div className="flex justify-end space-x-3 pt-6 border-t-2 border-gray-200 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditDialogOpen(false);
                      setEditingPregunta(null);
                    }}
                    className="px-6 py-3 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all"
                  >
                    <i className="fas fa-times mr-2"></i>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="instituto-gradient text-white px-6 py-3 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    disabled={updating}
                  >
                    {updating ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Actualizando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Actualizar Pregunta
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Formulario Info */}
      <Card className="shadow-lg border-0">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardTitle className="flex items-center justify-between">
            <span>{formulario.titulo}</span>
            <Badge variant="secondary" className="bg-purple-100 text-purple-800">
              Total: {formulario.puntuacion_total} pts
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-gray-600 mb-4">{formulario.descripcion}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <strong>Clase:</strong> {formulario.clase_titulo}
            </div>
            <div>
              <strong>Profesor:</strong> {formulario.profesor_nombre}
            </div>
            <div>
              <strong>Preguntas:</strong> {preguntas.length}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preguntas */}
      {preguntas.length > 0 ? (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Preguntas ({preguntas.length})</h2>
          {preguntas.map((pregunta, index) => {
            // Función para obtener el color y icono según el tipo
            const getTipoStyle = (tipo) => {
              switch (tipo) {
                case 'opcion_multiple':
                  return {
                    color: 'bg-blue-500',
                    bgColor: 'bg-blue-50 border-blue-200',
                    textColor: 'text-blue-700',
                    icon: 'fas fa-list-ul'
                  };
                case 'verdadero_falso':
                  return {
                    color: 'bg-green-500',
                    bgColor: 'bg-green-50 border-green-200',
                    textColor: 'text-green-700',
                    icon: 'fas fa-check-double'
                  };
                case 'respuesta_corta':
                  return {
                    color: 'bg-purple-500',
                    bgColor: 'bg-purple-50 border-purple-200',
                    textColor: 'text-purple-700',
                    icon: 'fas fa-pen'
                  };
                case 'respuesta_larga':
                  return {
                    color: 'bg-teal-500',
                    bgColor: 'bg-teal-50 border-teal-200',
                    textColor: 'text-teal-700',
                    icon: 'fas fa-align-left'
                  };
                case 'ensayo':
                  return {
                    color: 'bg-orange-500',
                    bgColor: 'bg-orange-50 border-orange-200',
                    textColor: 'text-orange-700',
                    icon: 'fas fa-file-alt'
                  };
                case 'fecha':
                  return {
                    color: 'bg-pink-500',
                    bgColor: 'bg-pink-50 border-pink-200',
                    textColor: 'text-pink-700',
                    icon: 'fas fa-calendar-day'
                  };
                case 'subir_audio':
                  return {
                    color: 'bg-red-500',
                    bgColor: 'bg-red-50 border-red-200',
                    textColor: 'text-red-700',
                    icon: 'fas fa-microphone'
                  };
                case 'subir_video':
                  return {
                    color: 'bg-indigo-500',
                    bgColor: 'bg-indigo-50 border-indigo-200',
                    textColor: 'text-indigo-700',
                    icon: 'fas fa-video'
                  };
                case 'link_wix':
                  return {
                    color: 'bg-yellow-500',
                    bgColor: 'bg-yellow-50 border-yellow-200',
                    textColor: 'text-yellow-700',
                    icon: 'fas fa-external-link-alt'
                  };
                case 'link_formulario_interno':
                  return {
                    color: 'bg-blue-500',
                    bgColor: 'bg-blue-50 border-blue-200',
                    textColor: 'text-blue-700',
                    icon: 'fas fa-file-alt'
                  };
                case 'link_youtube':
                  return {
                    color: 'bg-red-600',
                    bgColor: 'bg-red-50 border-red-200',
                    textColor: 'text-red-700',
                    icon: 'fab fa-youtube'
                  };
                default:
                  return {
                    color: 'bg-gray-500',
                    bgColor: 'bg-gray-50 border-gray-200',
                    textColor: 'text-gray-700',
                    icon: 'fas fa-question'
                  };
              }
            };

            const tipoStyle = getTipoStyle(pregunta.tipo);

            return (
              <Card key={pregunta.id} className={`shadow-lg border-2 ${tipoStyle.bgColor} hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      {/* Icono y número de pregunta */}
                      <div className="flex flex-col items-center">
                        <div className={`${tipoStyle.color} rounded-full w-12 h-12 flex items-center justify-center text-white shadow-lg`}>
                          <i className={`${tipoStyle.icon} text-lg`}></i>
                        </div>
                        <div className={`mt-2 px-3 py-1 rounded-full text-sm font-bold ${tipoStyle.color} text-white shadow`}>
                          #{index + 1}
                        </div>
                      </div>
                      
                      {/* Contenido de la pregunta */}
                      <div className="flex-1">
                        <CardTitle className={`text-xl leading-relaxed ${tipoStyle.textColor} font-semibold mb-3`}>
                          <div dangerouslySetInnerHTML={{ __html: pregunta.titulo }} />
                        </CardTitle>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge className={`text-xs font-medium ${tipoStyle.color} text-white`}>
                            <i className={`${tipoStyle.icon} mr-1`}></i>
                            {getTipoDisplayName(pregunta.tipo)}
                          </Badge>
                          <Badge variant="outline" className="text-xs font-bold bg-yellow-100 text-yellow-800 border-yellow-300">
                            <i className="fas fa-star mr-1"></i>
                            {pregunta.puntuacion} {pregunta.puntuacion === 1 ? 'punto' : 'puntos'}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Botones de acción */}
                    {(user.rol === 'admin' || user.id === formulario.profesor_id) && (
                      <div className="flex flex-col items-center space-y-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditPregunta(pregunta)}
                          className="text-blue-600 border-blue-300 hover:bg-blue-50 hover:border-blue-400 h-9 w-9 p-0 rounded-full shadow-sm"
                          title="Editar pregunta"
                        >
                          <i className="fas fa-edit text-sm"></i>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeletePregunta(pregunta.id, pregunta.titulo)}
                          className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 h-9 w-9 p-0 rounded-full shadow-sm"
                          title="Eliminar pregunta"
                        >
                          <i className="fas fa-trash text-sm"></i>
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
              <CardContent className="pt-0 ml-16">
                {/* Opciones para preguntas de opción múltiple y verdadero/falso */}
                {(pregunta.tipo === 'opcion_multiple' || pregunta.tipo === 'verdadero_falso') && 
                 pregunta.opciones && pregunta.opciones.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center mb-3">
                      <i className="fas fa-list text-gray-500 mr-2"></i>
                      <h4 className="text-sm font-semibold text-gray-700">Opciones disponibles:</h4>
                    </div>
                    <div className="grid gap-2">
                      {pregunta.opciones.map((opcion, i) => (
                        <div key={i} className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all
                          ${pregunta.respuesta_correcta === opcion 
                            ? 'border-green-300 bg-green-50 shadow-sm' 
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                              ${pregunta.respuesta_correcta === opcion 
                                ? 'bg-green-500 text-white' 
                                : 'bg-gray-200 text-gray-600'
                              }`}>
                              {String.fromCharCode(65 + i)}
                            </div>
                            <span className={`font-medium ${pregunta.respuesta_correcta === opcion ? 'text-green-800' : 'text-gray-700'}`}>
                              {opcion}
                            </span>
                          </div>
                          {pregunta.respuesta_correcta === opcion && (
                            <div className="flex items-center space-x-2">
                              <Badge className="bg-green-500 text-white text-xs">
                                <i className="fas fa-check-circle mr-1"></i>
                                Respuesta Correcta
                              </Badge>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Mostrar respuesta correcta para otros tipos */}
                {pregunta.tipo !== 'opcion_multiple' && pregunta.tipo !== 'verdadero_falso' && 
                 pregunta.respuesta_correcta && (
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <i className="fas fa-lightbulb text-yellow-500 mr-2"></i>
                      <h4 className="text-sm font-semibold text-gray-700">Respuesta modelo/esperada:</h4>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-2 border-green-200 shadow-sm">
                      <div className="flex items-start space-x-3">
                        <div className="bg-green-500 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="fas fa-check text-white text-sm"></i>
                        </div>
                        <div className="flex-1">
                          <span className="text-green-800 font-medium break-words">{pregunta.respuesta_correcta}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Información adicional para tipos especiales */}
                {(pregunta.tipo === 'subir_audio' || pregunta.tipo === 'subir_video') && (
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <i className="fas fa-info-circle text-blue-500 mr-2"></i>
                      <h4 className="text-sm font-semibold text-gray-700">Instrucciones para el estudiante:</h4>
                    </div>
                    <div className={`p-4 rounded-lg border-2 ${tipoStyle.bgColor} shadow-sm`}>
                      <div className="flex items-start space-x-3">
                        <div className={`${tipoStyle.color} rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0`}>
                          <i className={`${tipoStyle.icon} text-white text-sm`}></i>
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm ${tipoStyle.textColor}`}>
                            {pregunta.tipo === 'subir_audio' 
                              ? 'Los estudiantes podrán subir archivos de audio (MP3, WAV, AAC, OGG). Máximo 50MB.'
                              : 'Los estudiantes podrán subir archivos de video (MP4, AVI, MOV, WebM). Máximo 100MB.'
                            }
                          </p>
                          <p className="text-xs text-gray-600 mt-2">
                            Se otorga la puntuación completa por cualquier archivo válido subido.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {pregunta.tipo === 'respuesta_larga' && (
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <i className="fas fa-align-left text-teal-500 mr-2"></i>
                      <h4 className="text-sm font-semibold text-gray-700">Tipo de respuesta:</h4>
                    </div>
                    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border-2 border-teal-200 shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="bg-teal-500 rounded-full w-8 h-8 flex items-center justify-center">
                          <i className="fas fa-align-left text-white text-sm"></i>
                        </div>
                        <div>
                          <p className="text-teal-800 font-medium text-sm">Campo de texto extenso</p>
                          <p className="text-xs text-teal-600 mt-1">Los estudiantes pueden escribir respuestas largas y detalladas (hasta 2000 caracteres).</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {pregunta.tipo === 'ensayo' && (
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <i className="fas fa-pen-fancy text-orange-500 mr-2"></i>
                      <h4 className="text-sm font-semibold text-gray-700">Tipo de evaluación:</h4>
                    </div>
                    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border-2 border-orange-200 shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="bg-orange-500 rounded-full w-8 h-8 flex items-center justify-center">
                          <i className="fas fa-file-alt text-white text-sm"></i>
                        </div>
                        <div>
                          <p className="text-orange-800 font-medium text-sm">Evaluación manual requerida</p>
                          <p className="text-xs text-orange-600 mt-1">El profesor deberá calificar esta respuesta manualmente.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )})}
        </div>
      ) : (
        <Card className="shadow-lg border-0 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
          <CardContent className="text-center py-16">
            <div className="bg-blue-500 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <i className="fas fa-magic text-white text-4xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-blue-900 mb-3">¡Crea tu primer pregunta!</h3>
            <p className="text-blue-700 mb-6 max-w-md mx-auto">
              Transforma este formulario en una experiencia de aprendizaje increíble. 
              Agrega preguntas interactivas con diferentes tipos y estilos.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8 max-w-4xl mx-auto">
              <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-200">
                <i className="fas fa-list-ul text-blue-500 text-2xl mb-2"></i>
                <p className="text-xs text-blue-700 font-medium">Opción Múltiple</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm border border-green-200">
                <i className="fas fa-check-double text-green-500 text-2xl mb-2"></i>
                <p className="text-xs text-green-700 font-medium">Verdadero/Falso</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm border border-purple-200">
                <i className="fas fa-pen text-purple-500 text-2xl mb-2"></i>
                <p className="text-xs text-purple-700 font-medium">Respuesta Corta</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm border border-teal-200">
                <i className="fas fa-align-left text-teal-500 text-2xl mb-2"></i>
                <p className="text-xs text-teal-700 font-medium">Respuesta Larga</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm border border-orange-200">
                <i className="fas fa-file-alt text-orange-500 text-2xl mb-2"></i>
                <p className="text-xs text-orange-700 font-medium">Ensayo</p>
              </div>
            </div>
            <Button
              onClick={() => setIsDialogOpen(true)}
              className="instituto-gradient text-white btn-hover px-8 py-4 text-lg shadow-lg"
            >
              <i className="fas fa-plus-circle mr-3"></i>
              Crear Primera Pregunta
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GestionarFormulario;